import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

const HEADER_OFFSET = 128

/**
 * Al cambiar de ruta: si hay #apartado, se desplaza hasta él dejando espacio
 * para la barra fija y lo resalta brevemente. Si no, vuelve arriba.
 *
 * Cambios dentro de la misma página (elegir clase, estilo o un filtro, que
 * reemplazan los parámetros de la URL) NO mueven la página: el lector se queda
 * en la sección donde hizo clic.
 */
export function useHashScroll() {
  const { pathname, search, hash, key } = useLocation()
  const navType = useNavigationType()
  const prev = useRef<{ pathname: string; search: string; hash: string } | null>(null)

  useEffect(() => {
    const before = prev.current
    prev.current = { pathname, search, hash }
    const samePage = before !== null && before.pathname === pathname
    // Ajuste en el sitio (setParams con replace): conservar la posición.
    const inPlace = samePage && navType === 'REPLACE' && hash === before.hash
    if (inPlace) return

    if (!hash) {
      // Quitar el #apartado dentro de la misma página tampoco debe subir al inicio.
      if (samePage && before.search !== search && navType === 'REPLACE') return
      if (samePage && before.search === search) return
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
      return
    }
    const id = decodeURIComponent(hash.slice(1))
    let tries = 0
    let retry = 0
    let clear = 0
    const attempt = () => {
      const el = document.getElementById(id)
      if (!el) {
        if (tries++ < 20) retry = window.setTimeout(attempt, 50)
        return
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' })
      el.classList.remove('guide-flash')
      void el.offsetWidth
      el.classList.add('guide-flash')
      clear = window.setTimeout(() => el.classList.remove('guide-flash'), 1900)
    }
    attempt()
    return () => {
      window.clearTimeout(retry)
      window.clearTimeout(clear)
    }
  }, [pathname, search, hash, key, navType])
}
